import {useEffect, useRef, useState} from 'react';
import {useNotifications} from '@/hooks/useNotifications';
import {usePermissions} from '@/hooks/usePermissions';
import {logger} from '@/lib/logger';
import {sendLindaChat} from '@/lib/linda-chat-service';

import {useChatState, useChatActions, useChatResize} from './hooks';
import {
    ChatHeader,
    MessageBubble,
    ChatInput,
    LoadingIndicator,
    ActionIndicator,
} from './components';
import type {ChatMessage} from './types';
import {getModelbankToken} from '@/util';
import styles from './style.module.css';

const log = logger.tag('LindaChatPanel');
log.info('Chat Panel loaded - version 2.0');

function LindaChatPanelInner() {
    const {
        isOpen,
        messages,
        isLoading,
        setIsLoading,
        panelWidth,
        setPanelWidth,
        addMessage,
        clearMessages,
        closeChat,
        user,
        notifications,
        selectedSupplierId,
        getContext,
    } = useChatState();

    const {currentAction, executeAction} = useChatActions();
    const {startResizing} = useChatResize({panelWidth, setPanelWidth});
    const {addNotification} = useNotifications();

    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [messages]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();

        const messageText = inputValue.trim();
        if (!messageText || isLoading) return;

        // Add user message immediately
        addMessage({
            role: 'user',
            content: messageText,
        });

        // Clear input
        setInputValue('');
        setIsLoading(true);

        try {
            // Prepare conversation history (last 20 messages)
            const conversationHistory = messages.slice(-20).map((msg) => ({
                role: msg.role,
                content: msg.content,
            }));

            // Add the new user message to history
            conversationHistory.push({
                role: 'user',
                content: messageText,
            });

            // Get current context
            const context = getContext();

            log.debug('Sending message to LiteLLM...');

            const data = await sendLindaChat({
                message: messageText,
                conversationHistory,
                context,
                notifications: notifications.map((n) => ({
                    type: n.type,
                    status: n.status,
                    title: n.title,
                    productName: n.productName,
                })),
                tokens: {
                    modelbank_token: getModelbankToken(),
                    auth_token: user?.auth_token ?? '',
                },
                selectedSupplierId: selectedSupplierId
                    ? String(selectedSupplierId)
                    : undefined,
            });
            log.debug('API response received', {
                hasMessage: !!data.message,
                messageLength: data.message?.length,
                hasSearchResults: !!data.searchResults,
                hasActions: !!data.actions,
            });

            // Add Linda's response with search results if present
            addMessage({
                role: 'assistant',
                content: data.message,
                searchResults: data.searchResults,
            });
            log.debug('Message added to history');

            // Create notification if Linda started a render job
            if (data.notification) {
                addNotification(data.notification);
            }

            // Execute any actions
            if (data.actions && data.actions.length > 0) {
                for (const action of data.actions) {
                    await executeAction(action);
                }
            }
        } catch (error) {
            log.error('API error', error);
            // Add error message
            addMessage({
                role: 'assistant',
                content:
                    'I apologize, but I encountered an error. Please try again.',
            });
        } finally {
            log.debug('Setting loading to false');
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            ref={panelRef}
            className={`${styles.chatPanel} ${styles.open}`}
            style={{width: `${panelWidth}px`}}
        >
            {/* Resize Handle */}
            <div className={styles.resizeHandle} onMouseDown={startResizing} />

            {/* Header */}
            <ChatHeader
                hasMessages={messages.length > 0}
                onClearMessages={clearMessages}
                onClose={closeChat}
            />

            {/* Messages */}
            <div className={styles.messagesContainer}>
                {messages.length === 0 ? (
                    <div className={styles.emptyState}>
                        Hi! I'm Linda. Ask me anything about ModelBank or your
                        assets.
                    </div>
                ) : (
                    messages.map((message) => (
                        <MessageBubble
                            key={message.id}
                            message={message as ChatMessage}
                        />
                    ))
                )}

                {isLoading && <LoadingIndicator />}

                {currentAction && <ActionIndicator action={currentAction} />}

                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
                value={inputValue}
                onChange={setInputValue}
                onSubmit={handleSendMessage}
                isLoading={isLoading}
                isOpen={isOpen}
            />
        </div>
    );
}

export function LindaChatPanel() {
    const {hasAccess} = usePermissions();

    if (!hasAccess('view-linda')) return null;

    return <LindaChatPanelInner />;
}
